import { Button } from "@dropbox/dig-components/buttons";
import { FormLabel, FormRow } from "@dropbox/dig-components/form_row";
import { Modal } from "@dropbox/dig-components/modal";
import { TextArea, TextInput } from "@dropbox/dig-components/text_fields";
import { Text } from "@dropbox/dig-components/typography";
import { Box } from "@dropbox/dig-foundations";
import { UIIcon } from "@dropbox/dig-icons";
import { CheckmarkLine, FailLine } from "@dropbox/dig-icons/assets";
import { PAPEvent } from "@mirage/analytics/events/base/event";
import { ActionSurfaceComponent } from "@mirage/analytics/events/enums/action_surface_component";
import { PAP_Send_DropboxAiFeedback } from "@mirage/analytics/events/types/send_dropbox_ai_feedback";
import { Link } from "@mirage/link/Link";
import useDropboxAccount from "@mirage/service-auth/useDropboxAccount";
import { getConversationByServerId } from "@mirage/service-conversation";
import { ConversationWithId } from "@mirage/service-conversation/types";
import { callApiV2 } from "@mirage/service-dbx-api";
import {
  isDropboxerAccount,
  IsDropboxerMode,
  isTestAccount,
} from "@mirage/shared/account";
import { useDashActionSurface } from "@mirage/shared/hooks/useDashActionSurface";
import { showSnackbar } from "@mirage/shared/snackbar";
import { isEmbedPage } from "@mirage/shared/util/tiny-utils";
import i18n, { Trans } from "@mirage/translations";
import { ChangeEvent, useEffect, useState } from "react";
import { generateConversationLog } from "./utils";

type ConversationFeedbackModalProps = {
  conversationId: string;
  isOpen: boolean;
  logComposeEvent: (
    event: PAPEvent,
    overrides?: { actionSurfaceComponent?: ActionSurfaceComponent },
  ) => void;
  onClose: () => void;
  spanId?: string;
};

export const ConversationFeedbackModal = ({
  conversationId,
  isOpen,
  logComposeEvent,
  onClose,
  spanId,
}: ConversationFeedbackModalProps) => {
  const currentAccount = useDropboxAccount();
  const { dashActionSurface } = useDashActionSurface();

  const [conversation, setConversation] = useState<ConversationWithId>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [ticketTitle, setTicketTitle] = useState("");
  const [userFeedback, setUserFeedback] = useState("");

  const isInternalUser =
    isDropboxerAccount(currentAccount, IsDropboxerMode.WorkAccountOnly) ||
    isTestAccount(currentAccount);

  useEffect(() => {
    const fetchConversation = async () => {
      // Fetch the conversation by its server ID. By this point the conversation will have
      // always been loaded, so we can safely assume this will be a cache read 100% of the time.
      const result = await getConversationByServerId(conversationId);
      setConversation(result);
    };

    if (conversationId) {
      void fetchConversation();
    }
  }, [conversationId]);

  const handleClose = () => {
    // If feedback is being submitted, keep the modal open, otherwise wipe the feedback and
    // invoke the onClose callback.
    if (!isSubmitting) {
      setUserFeedback("");
      onClose();
    }
  };

  const handleSubmitFeedback = async () => {
    try {
      setIsSubmitting(true);

      await callApiV2("contextEngineAssistApiFeedback", {
        conversation_id: conversationId,
        conversation_log: generateConversationLog(conversation?.messages || []),
        feedback: userFeedback,
        span_id: spanId,
        ticket_title: isInternalUser && ticketTitle ? ticketTitle : undefined,
        request_id: conversation?.assistRequestId,
        tags: isEmbedPage() ? ["bridge"] : [],
      });

      logComposeEvent(
        PAP_Send_DropboxAiFeedback({
          conversationId,
          dashActionSurface,
          featureLine: "assist",
          openFeedback: userFeedback,
        }),
      );

      showSnackbar({
        accessory: <UIIcon src={CheckmarkLine} />,
        title: i18n.t("feedback_received"),
      });

      handleClose();
    } catch (error) {
      showSnackbar({
        accessory: <UIIcon src={FailLine} />,
        title: i18n.t("feedback_failed"),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal isCentered open={isOpen} onRequestClose={handleClose}>
      <Modal.Header>
        <Text size="xlarge">{i18n.t("conversation_feedback_modal_title")}</Text>
        <Box alignItems="center" display="flex" marginBottom="16">
          <Text size="medium" color="subtle">
            {i18n.t("conversation_feedback_modal_subtitle")}
          </Text>
        </Box>
      </Modal.Header>
      <Modal.Body>
        {!isInternalUser && (
          <Box marginBottom="20">
            <Text color="standard" size="medium">
              <Trans
                i18nKey="conversation_feedback_modal_external_messaging"
                components={{
                  ppLink: (
                    <Link
                      href="https://help.dropbox.com/dash?_tk=dash-conversation-feedback"
                      target="_blank"
                    />
                  ),
                }}
              />
            </Text>
          </Box>
        )}
        {isInternalUser && (
          <FormRow>
            <FormLabel
              htmlFor="conversation-feedback-ticket-title-input"
              subtext={i18n.t("conversation_feedback_modal_optional")}
            >
              {i18n.t("conversation_feedback_modal_ticket_title_label")}
            </FormLabel>
            <TextInput
              data-testid="conversation-feedback-ticket-title-input"
              id="conversation-feedback-ticket-title-input"
              maxLength={60}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                setTicketTitle(e.target.value)
              }
              placeholder={i18n.t(
                "conversation_feedback_modal_ticket_title_placeholder",
              )}
              value={ticketTitle}
            />
          </FormRow>
        )}
        <FormRow>
          <FormLabel
            aria-required
            htmlFor="conversation-feedback-description-input"
            subtext={i18n.t("conversation_feedback_modal_required")}
          >
            {i18n.t("conversation_feedback_modal_feedback_label")}
          </FormLabel>
          <TextArea
            className="meticulous-redact-recording"
            data-testid="conversation-feedback-description-input"
            id="conversation-feedback-description-input"
            onChange={(e: ChangeEvent<HTMLTextAreaElement>) =>
              setUserFeedback(e.target.value)
            }
            placeholder={i18n.t(
              "conversation_feedback_modal_feedback_placeholder",
            )}
            value={userFeedback}
          />
        </FormRow>
        <Box>
          <Text color="subtle" size="small" style={{ fontStyle: "italic" }}>
            <Trans
              i18nKey="conversation_feedback_modal_disclaimer"
              components={{
                ppLink: (
                  <Link
                    href="https://www.dropbox.com/privacy"
                    target="_blank"
                  />
                ),
              }}
            />
          </Text>
        </Box>
      </Modal.Body>
      <Modal.Footer>
        <Button onClick={onClose} variant="opacity">
          {i18n.t("cancel")}
        </Button>
        <Button
          disabled={!userFeedback}
          isLoading={isSubmitting}
          onClick={handleSubmitFeedback}
          variant="primary"
        >
          {i18n.t("conversation_feedback_modal_submit_button")}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
